// react
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

// mui
import { Stack, Typography } from '@mui/material';

// components
import {
  GenericFlowProps,
  OnboardingActions,
  OnboardingActionType,
  toGenericOnboarding,
} from '@/components/pages/OnboardingFlow/OnboardingFlow';
import { Checkbox } from '@/components/atoms/Checkbox/Checkbox';
import { Container } from '@/components/atoms/Container/Container';
import { Icon } from '@/components/atoms/Icon/Icon';

import { Editor } from '@/components/organisms/Editor/Editor';
import {
  createFieldHelper,
  FieldDef,
} from '@/components/organisms/EditorFields/fieldHelper';
import Tooltip from '@/components/atoms/Tooltip/Tooltip';

export const OnboardingEditor = toGenericOnboarding(
  ({
    currentIndex = 0,
    config,
    isNextDisabled,
    setIsNextDisabled,
    handleClick,
  }: GenericFlowProps<OnboardingEditorProps>) => {
    // memos
    const currentConfig = useMemo(
      () => config[currentIndex],
      [config, currentIndex],
    );

    // refs
    const formRef = useRef<HTMLFormElement>(null);

    const fields = useMemo(() => {
      return createFieldHelper<{ value: string }>({
        value: currentConfig?.fieldDef,
      });
    }, [currentConfig]);

    const value = useMemo(() => currentConfig?.value, [currentConfig]);
    const setValue = useMemo(() => currentConfig?.onChange, [currentConfig]);

    // effects
    // Programmatic focus after framer-motion slide animation (250ms) completes.
    // Native autoFocus doesn't work because the element mounts while still hidden.
    useEffect(() => {
      const timer = setTimeout(() => {
        const input = formRef.current?.querySelector<HTMLElement>(
          'input, textarea, select',
        );
        input?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }, [currentIndex]);

    useEffect(() => {
      if (!config?.[currentIndex]?.fieldDef?.validation) {
        setIsNextDisabled(false);
        return;
      }
      const isFieldInvalid =
        !config?.[currentIndex]?.fieldDef?.validation?.(value?.value) || false;
      const consent = config?.[currentIndex]?.consent;
      const isConsentUnsatisfied = consent ? !consent.value : false;
      setIsNextDisabled(isFieldInvalid || isConsentUnsatisfied);
    }, [value, currentIndex, config]);

    return (
      <StyledContainer size='md' isScroll>
        <Wrapper>
          {config && (
            <>
              <Stack spacing={1}>
                <Typography variant='h2'>{currentConfig?.title}</Typography>
                {(currentConfig?.subTitle || currentConfig?.tooltip) && (
                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{
                      alignItems: 'flex-end',
                    }}
                  >
                    {currentConfig?.subTitle && (
                      <Typography variant='subtitle1'>
                        {currentConfig?.subTitle}
                      </Typography>
                    )}
                    {currentConfig?.tooltip && (
                      <StyledTooltip content={currentConfig?.tooltip}>
                        <TooltipIcon>
                          <Icon icon={'info-circle'} fontSize={24} />
                        </TooltipIcon>
                      </StyledTooltip>
                    )}
                  </Stack>
                )}
              </Stack>
              {fields && (
                <form
                  ref={formRef}
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      !isNextDisabled &&
                        handleClick?.(OnboardingActionType.Next);
                    }
                  }}
                >
                  <Editor
                    fields={fields}
                    value={value}
                    onChange={setValue}
                    isFloatingActions={false}
                    isActionsDisabled={true}
                    isForm={false}
                  />
                </form>
              )}
              {currentConfig?.consent && (
                <Consent>
                  <Checkbox
                    label={currentConfig?.consent?.label}
                    value={currentConfig?.consent?.value}
                    onChange={currentConfig?.consent?.onChange}
                  />
                </Consent>
              )}
            </>
          )}
        </Wrapper>
      </StyledContainer>
    );
  },
);

const Consent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  color: ${(props) => props.theme.color.neutral[80]};
  font: ${(props) => props.theme.typography.body.sm.regular};
  margin-top: ${(props) => props.theme.spacing[16]};
`;

const StyledContainer = styled(Container)`
  height: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  min-height: content;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[16]};
  padding: ${(props) => props.theme.spacing[32]} 0;
`;

const StyledTooltip = styled(Tooltip)``;

const TooltipIcon = styled.div`
  color: ${(props) => props.theme.color.neutral[70]};
`;

export interface OnboardingEditorProps extends OnboardingActions {
  title: string;
  subTitle: string;
  tooltip?: string;
  value: any;
  onChange: (value: any) => void;
  fieldDef: FieldDef;
  isDisclaimer?: boolean;
  consent?: {
    label: string;
    value: boolean;
    onChange: (value: boolean) => void;
  };
}

OnboardingEditor.displayName = 'OnboardingEditor';

export default toGenericOnboarding(OnboardingEditor);
